import React, { useState } from 'react';
import { Plus, X, Globe, DollarSign, Clock, AlertCircle } from 'lucide-react';

const CreateSiteModal = ({ onCreate, onCancel }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    domain: '',
    currency: 'EUR',
    prebidTimeout: 2000
  });
  const [errors, setErrors] = useState({});
  const [isCreating, setIsCreating] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Site ID validation
    if (!formData.id.trim()) {
      newErrors.id = 'Site ID is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.id)) {
      newErrors.id = 'Site ID can only contain lowercase letters, numbers, and hyphens';
    } else if (formData.id.length < 3) {
      newErrors.id = 'Site ID must be at least 3 characters';
    } else if (formData.id.length > 20) {
      newErrors.id = 'Site ID must be no more than 20 characters';
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Site name is required';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Site name must be no more than 50 characters';
    }

    // Domain validation
    if (!formData.domain.trim()) {
      newErrors.domain = 'Domain is required';
    } else if (!/^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/.test(formData.domain)) {
      newErrors.domain = 'Please enter a valid domain (e.g., example.com)';
    }

    // Currency validation
    if (!formData.currency.trim()) {
      newErrors.currency = 'Currency is required';
    }

    // Timeout validation
    if (!formData.prebidTimeout || formData.prebidTimeout < 500 || formData.prebidTimeout > 5000) {
      newErrors.prebidTimeout = 'Prebid timeout must be between 500 and 5000 milliseconds';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsCreating(true);
    try {
      // Transform field names to match API expectations
      const apiData = {
        siteId: formData.id,
        siteName: formData.name,
        domain: formData.domain,
        currency: formData.currency,
        prebidTimeout: formData.prebidTimeout
      };
      await onCreate(apiData);
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsCreating(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleIdChange = (value) => {
    // Auto-generate ID from name if ID is empty
    if (!formData.id || formData.id === generateIdFromName(formData.name)) {
      const newId = generateIdFromName(value);
      handleInputChange('id', newId);
    }
    handleInputChange('name', value);
  };

  const generateIdFromName = (name) => {
    return name
      .toLowerCase()
      .replace(/\.([a-z]{2,4})(\/.*)?$/g, '') // Remove domain extensions (.com, .ro, .org, etc.)
      .replace(/[^a-z0-9\s-]/g, '') // Remove special chars except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
      .substring(0, 20); // Limit length
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Plus className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Create New Site</h3>
              <p className="text-sm text-gray-500">Add a new site to your Prebid configuration</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isCreating}
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Site Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Site Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleIdChange(e.target.value)}
              placeholder="e.g., Love Deco"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                errors.name 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              disabled={isCreating}
            />
            {errors.name && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle size={12} />
                {errors.name}
              </p>
            )}
          </div>

          {/* Site ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Site ID *
            </label>
            <input
              type="text"
              value={formData.id}
              onChange={(e) => handleInputChange('id', e.target.value)}
              placeholder="e.g., lovedeco"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                errors.id 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              disabled={isCreating}
            />
            <p className="text-xs text-gray-500 mt-1">
              Used in configurations and URLs. Only lowercase letters, numbers, and hyphens.
            </p>
            {errors.id && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle size={12} />
                {errors.id}
              </p>
            )}
          </div>

          {/* Domain */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Globe size={14} className="inline mr-1" />
              Domain *
            </label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => handleInputChange('domain', e.target.value)}
              placeholder="e.g., lovedeco.ro"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                errors.domain 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              disabled={isCreating}
            />
            {errors.domain && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle size={12} />
                {errors.domain}
              </p>
            )}
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <DollarSign size={14} className="inline mr-1" />
              Currency
            </label>
            <select
              value={formData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isCreating}
            >
              <option value="EUR">EUR - Euro</option>
              <option value="USD">USD - US Dollar</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="RON">RON - Romanian Leu</option>
            </select>
          </div>

          {/* Prebid Timeout */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Clock size={14} className="inline mr-1" />
              Prebid Timeout (ms)
            </label>
            <input
              type="number"
              value={formData.prebidTimeout}
              onChange={(e) => handleInputChange('prebidTimeout', parseInt(e.target.value))}
              min="500"
              max="5000"
              step="100"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                errors.prebidTimeout 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              disabled={isCreating}
            />
            <p className="text-xs text-gray-500 mt-1">
              How long to wait for bid responses (500-5000ms, recommended: 2000ms)
            </p>
            {errors.prebidTimeout && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle size={12} />
                {errors.prebidTimeout}
              </p>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 flex items-center gap-2">
                <AlertCircle size={16} />
                {errors.submit}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                isCreating
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isCreating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus size={16} />
                  Create Site
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSiteModal;